/**
 * premiumService — Read subscription status from Firestore.
 */

import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import type { PlanStatus, PlanType, PremiumStatus, WebPricing } from '../types/premium';

/** Firestore Timestamp | ms | ISO → Date, or null when absent/invalid. */
function toDate(v: unknown): Date | null {
  if (!v) return null;
  const maybe = v as { toDate?: () => Date };
  if (typeof maybe.toDate === 'function') {
    try { return maybe.toDate(); } catch { return null; }
  }
  const d = new Date(v as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A recognised planStatus, else null (never trust free strings from a doc). */
function rawPlanStatus(v: unknown): PlanStatus | null {
  return v === 'active' || v === 'cancelled' || v === 'expired' ? v : null;
}

/** Get the user's premium / subscription status */
export async function getPremiumStatus(uid: string): Promise<PremiumStatus> {
  const userDoc = await getDoc(doc(FIRESTORE_DB, 'users', uid));
  const userData = userDoc.data() || {};

  /**
   * PPL-7 — `users/{uid}.selectedPlan` is the server-authoritative entitlement:
   * only the RevenueCat webhook, setPremiumStatusEurW and the PayFast ITN write
   * it, via the Admin SDK. `users/{uid}/settings/isPremium` carries richer
   * metadata but is legacy and client-writable, so it may only FILL IN when
   * selectedPlan is absent — it must never override it.
   */
  const selectedPlanRaw = typeof userData.selectedPlan === 'string' && userData.selectedPlan
    ? userData.selectedPlan
    : null;
  const selectedPlan = selectedPlanRaw || 'free';

  // PPL-357 trial + PPL-350 lapse markers, stamped server-side on the user doc.
  // Surfaced verbatim; the premium page validates the timestamps before use.
  const trialFields = {
    trialStatus: typeof userData.trialStatus === 'string' ? userData.trialStatus : null,
    trialStartedAt: userData.trialStartedAt ?? null,
    trialEndsAt: userData.trialEndsAt ?? null,
    lapsedAt: userData.lapsedAt ?? null,
  };

  // Check users/{uid}/settings/isPremium for detailed subscription info
  const premiumDoc = await getDoc(doc(FIRESTORE_DB, 'users', uid, 'settings', 'isPremium'));
  if (premiumDoc.exists()) {
    const data = premiumDoc.data();
    // Same predicate the server uses everywhere (rememberedQuotaFor, the
    // entitlement module's isPaidPremium, every gate): a non-empty plan that
    // isn't 'free' counts as paid. Only 'premium' is written today, but a
    // strict === 'premium' here would drift the moment a second paid plan
    // string appears server-side.
    //
    // The settings doc is client-writable, so when it is the ONLY evidence it
    // may grant premium only with a verifiable, still-future expirationDate —
    // a doc that merely says `isPremium: true` (stale or forged) sells as
    // free. Mirrors the app's premiumStatus.ts (PPL-353 review parity).
    const settingsExpiry = toDate(data.expirationDate || data.expiry);
    const settingsGrantValid =
      data.isPremium === true && settingsExpiry !== null && settingsExpiry.getTime() > Date.now();
    const isPremium = selectedPlanRaw !== null
      ? selectedPlanRaw !== 'free'
      : settingsGrantValid;

    const store = data.store || null;
    // The server writes lowercase app_store / play_store; older docs carried
    // uppercase — compare case-insensitively or app subs are misclassified.
    const storeLc = typeof store === 'string' ? store.toLowerCase() : '';
    const isAppStore = storeLc === 'app_store' || storeLc === 'play_store';

    // App-store subs carry their billing cycle in the RevenueCat
    // productIdentifier ("...annual..." / "...yearly..."); periodType is only
    // reliable for web subscriptions.
    const productId = typeof data.productIdentifier === 'string' ? data.productIdentifier : '';
    const planType: PlanType = isAppStore
      ? (/annual|yearly/i.test(productId) ? 'yearly' : 'monthly')
      : (data.periodType === 'P1Y' || data.periodType === 'yearly' ? 'yearly' : 'monthly');

    // Cancelled-but-still-active is a server-known state: prefer the explicit
    // planStatus / cancelledAt the backend stamps over deriving from isPremium.
    const explicitStatus = rawPlanStatus(data.planStatus) ?? rawPlanStatus(userData.planStatus);

    return {
      isPremium,
      plan: isPremium ? 'premium' : 'free',
      selectedPlan,
      planType,
      planStatus: explicitStatus ?? (isPremium ? 'active' : 'expired'),
      cancelledAt: data.cancelledAt ?? userData.cancelledAt ?? null,
      expiry: data.expirationDate || data.expiry || null,
      store,
      transactionId: data.transactionId || null,
      ...trialFields,
    };
  }

  const paidBySelectedPlan = selectedPlanRaw !== null && selectedPlanRaw !== 'free';
  return {
    isPremium: paidBySelectedPlan,
    plan: paidBySelectedPlan ? 'premium' : 'free',
    selectedPlan,
    planStatus: rawPlanStatus(userData.planStatus) ?? undefined,
    cancelledAt: userData.cancelledAt ?? null,
    ...trialFields,
  };
}

/**
 * Fetch pricing from global settings.
 *
 * **USD is the price.** It is chosen, not computed, so it never moves on its own.
 * `monthlyPriceZAR` / `yearlyPriceZAR` are the amounts Payfast actually bills, set
 * to match; when the rand moves materially someone updates the ZAR figures, and
 * existing subscribers are unaffected because Payfast fixes a subscription's amount
 * at signup.
 *
 * It used to work the other way round: a hand-set USD number sat beside an unrelated
 * ZAR charge with nothing linking them (PPL-344). Deriving USD from ZAR instead was
 * the obvious repair and it is the wrong one — the displayed price then wanders with
 * the exchange rate, which is precisely what a price must not do. So the direction is
 * inverted: pick the price, derive the charge.
 *
 * Payfast still converts at its own rate at checkout (MCP is enabled), so the buyer
 * sees roughly this number there. Keeping the ZAR figures current is what keeps
 * "roughly" close. The real fix is billing in USD directly, which is P5.
 */
export async function getPricing(): Promise<WebPricing> {
  const pricingDoc = await getDoc(doc(FIRESTORE_DB, 'global_settings', 'webPricing'));
  const data = pricingDoc.exists() ? pricingDoc.data() : {};

  return {
    usdMonthly: num(data.usdMonthly, DEFAULT_USD_MONTHLY),
    usdYearly: num(data.usdYearly, DEFAULT_USD_YEARLY),
  };
}

/**
 * Fallbacks, kept in step with global_settings/webPricing.
 *
 * Exported because Premium.tsx needs the same numbers for its render-time fallback.
 * Duplicating them there would recreate in miniature the exact defect this change
 * removes: two independently editable copies of one price.
 *
 * PPL-356: the yearly fallback is $59.99 — the reconciled pair is $9.99 / $59.99.
 *
 * SHIP GATE (ops step, logged on PPL-352/356): before this page goes live,
 * `global_settings/webPricing` must be updated in BOTH currencies — the
 * `usdMonthly`/`usdYearly` display pair AND the `monthlyPriceZAR`/`yearlyPriceZAR`
 * amounts PayFast actually bills — or the advertised price and the charge drift
 * apart. Served config always wins over these constants; they render only when
 * the doc is missing or unreadable.
 */
export const DEFAULT_USD_MONTHLY = 9.99;
export const DEFAULT_USD_YEARLY = 59.99;

/** A finite, positive number, else the fallback. */
function num(raw: unknown, fallback: number): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Check token allowance from global settings */
export async function getTokenLimits(): Promise<{ freeLimit: number; premiumLimit: number }> {
  const limitsDoc = await getDoc(doc(FIRESTORE_DB, 'global_settings', 'limits'));
  if (limitsDoc.exists()) {
    const data = limitsDoc.data();
    return {
      freeLimit: data.freeTokens ?? 20,
      premiumLimit: data.premiumTokens ?? -1, // -1 = unlimited
    };
  }
  return { freeLimit: 20, premiumLimit: -1 };
}
